import { useMemo, useState } from 'react';

import { ButtonSwitcher } from '../../../../../../components/button-switcher';
import { Dialog, DialogContent, DialogTitle } from '../../../../../../components/dialog';
import { PlanCardExtended } from '../../../../../../components/plan-card-extended';
import { getConfig } from '../../../../../../config';
import {
  type Currency,
  type Plan,
  PlanKind,
  SubscriptionPaymentCycle,
} from '../../../../../../entity/api/organization-billing';
import { tNamespace } from '../../../../../../services/translation';
import { formatDateToDdMmYyyy } from '../../../../../../shared/date';
import { SwitchKind, classifySwitch, getPlanFeatures } from '../../../../../../shared/subscription';

const tBilling = tNamespace('app.billing.general');
const config = getConfig();

export type EditPlanModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currency: Currency;
  plans: Plan[];
  discountPercent: number;
  userLanguage?: string;
  onPlanSelect?: (planId: string, billingPeriod: SubscriptionPaymentCycle) => void;
  currentPlanId?: string;
  currentPlanKind?: PlanKind;
  currentPaymentCycle?: SubscriptionPaymentCycle;
  currentPlanEndDate?: number;
  upgradeLoading?: boolean;
  isTrialing?: boolean;
};

export function EditPlanModal(props: EditPlanModalProps) {
  const {
    open,
    onOpenChange,
    currency,
    plans,
    discountPercent,
    onPlanSelect,
    currentPlanId,
    currentPlanKind,
    currentPaymentCycle,
    currentPlanEndDate = 0,
    upgradeLoading = false,
    isTrialing = false,
  } = props;

  const [billingPeriod, setBillingPeriod] = useState<SubscriptionPaymentCycle>(
    currentPaymentCycle ?? SubscriptionPaymentCycle.Month
  );
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);

  const filteredPlans = useMemo(
    function () {
      return plans.filter(function (plan: Plan) {
        return plan.paymentCycle === billingPeriod;
      });
    },
    [plans, billingPeriod]
  );

  const scaleYearlyDiscount = useMemo(
    function () {
      const plan = plans.find(function (p: Plan) {
        return p.kind === PlanKind.Scale && p.paymentCycle === SubscriptionPaymentCycle.Year;
      });
      return plan?.priceDiscount ?? discountPercent;
    },
    [plans, discountPercent]
  );

  function handlePlanSelect(planId: string) {
    if (onPlanSelect != null) {
      setLoadingPlanId(planId);
      onPlanSelect(planId, billingPeriod);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent hasCloseButton className="w-full max-w-[900px]">
        <div className="flex flex-col gap-6">
          {/* Title */}
          <DialogTitle align="center">{tBilling('editPlan')}</DialogTitle>

          {/* Billing Period Switcher */}
          <div className="flex justify-end">
            <ButtonSwitcher
              options={[
                { value: SubscriptionPaymentCycle.Month, label: tBilling('monthly') },
                {
                  value: SubscriptionPaymentCycle.Year,
                  label: tBilling('yearlyWithDiscount', { percent: scaleYearlyDiscount }),
                },
              ]}
              value={billingPeriod}
              onChange={function (value) {
                setBillingPeriod(value as SubscriptionPaymentCycle);
              }}
            />
          </div>

          {/* Plan Cards */}
          <div className="flex justify-center gap-4">
            {filteredPlans.map(function (plan) {
              const isCurrentPlan = currentPlanId === plan.providerPlanId;
              const isBestValue = plan.kind === PlanKind.Scale;

              let ctaText: string | undefined;
              let ctaSubLabel: string | undefined;
              let ctaLoading = false;

              if (isCurrentPlan) {
                ctaText = undefined;
              } else {
                const switchKind =
                  currentPlanKind != null && currentPaymentCycle != null
                    ? classifySwitch(
                        { kind: currentPlanKind, paymentCycle: currentPaymentCycle },
                        { kind: plan.kind as PlanKind, paymentCycle: plan.paymentCycle }
                      )
                    : SwitchKind.Upgrade;

                if (switchKind === SwitchKind.Upgrade || switchKind === SwitchKind.Same) {
                  ctaText = tBilling('upgrade');
                  ctaSubLabel = tBilling('effectiveImmediately');
                } else if (switchKind === SwitchKind.Downgrade) {
                  ctaText = tBilling('downgrade');
                  ctaSubLabel =
                    !isTrialing && currentPlanEndDate > 0
                      ? tBilling('effectiveOn', {
                          date: formatDateToDdMmYyyy(new Date(currentPlanEndDate)),
                        })
                      : tBilling('effectiveImmediately');
                }

                ctaLoading = upgradeLoading && loadingPlanId === plan.providerPlanId;
              }

              return (
                <PlanCardExtended
                  key={plan.providerPlanId}
                  planId={plan.providerPlanId}
                  title={plan.kind}
                  originalPrice={plan.pricePerMonth}
                  currentPrice={plan.pricePerMonth}
                  paymentCycle={
                    billingPeriod === SubscriptionPaymentCycle.Year
                      ? SubscriptionPaymentCycle.Year
                      : SubscriptionPaymentCycle.Month
                  }
                  currency={currency}
                  isBestValue={isBestValue}
                  isCurrentPlan={isCurrentPlan}
                  ctaText={ctaText}
                  ctaSubLabel={ctaSubLabel}
                  ctaLoading={ctaLoading}
                  onCtaClick={function () {
                    handlePlanSelect(plan.providerPlanId);
                  }}
                  features={getPlanFeatures(plan)}
                  className="max-w-[280px] flex-1"
                />
              );
            })}
          </div>

          {/* See full feature comparison link */}
          <div className="flex justify-center">
            <a
              href={config.urlWebsitePriceComparison}
              target="_blank"
              rel="noopener noreferrer"
              className="font-body-2-m text-branding-accent-600 hover:text-branding-accent-700"
            >
              {tBilling('seeFullFeatureComparison')} →
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
